/* eslint-disable react/react-in-jsx-scope */
import { Button } from "primereact/button";
import { Card } from "primereact/card";
import { Column } from "primereact/column";
import { DataTable } from "primereact/datatable";
import { Divider } from "primereact/divider";
import { InputText } from "primereact/inputtext";
import { SplitButton } from "primereact/splitbutton";
import { Tag } from "primereact/tag";
import { Toast } from "primereact/toast";
import { useNavigate, useParams } from "react-router-dom";
import { useRef, useState } from "react";
import { ScenarioStatus } from "../../_shared/types/types";
import { formatPhone } from "../../_shared/services/UtilsService";

// Tipos para os dados mockados
interface MockScenario {
  scenario_name: string;
  is_make_active: boolean;
  test_mode: boolean;
  status: ScenarioStatus;
  connectedPhone: string;
  data_source: string;
}

export default function ChangelogScreen() {
  const navigate = useNavigate();
  const { id } = useParams();
  const toast = useRef<Toast>(null);
  
  // Estado para a busca de demonstração
  const [demoSearch, setDemoSearch] = useState<string>("");

  // Funções de navegação
  const goBack = () => {
    navigate(`/${id}`);
  };

  const clearDemoSearch = () => {
    setDemoSearch("");
  };

  // Dados mockados para demonstração
  const mockScenarios: MockScenario[] = [
    {
      scenario_name: "Cenário Google Maps - Restaurantes",
      is_make_active: true,
      test_mode: false,
      status: ScenarioStatus.Running,
      connectedPhone: "+55 11 99999-9999",
      data_source: "googlemaps"
    },
    {
      scenario_name: "Cenário Lista - Leads Imobiliários",
      is_make_active: true,
      test_mode: true,
      status: ScenarioStatus.Running,
      connectedPhone: "+55 11 88888-8888",
      data_source: "list"
    },
    {
      scenario_name: "Cenário CRM - Vendas",
      is_make_active: false,
      test_mode: false,
      status: ScenarioStatus.Paused,
      connectedPhone: "+55 11 77777-7777",
      data_source: "crm"
    },
    {
      scenario_name: "Cenário Desconectado",
      is_make_active: true,
      test_mode: false,
      status: ScenarioStatus.InstanceDisconnected,
      connectedPhone: "disconnected",
      data_source: "googlemaps"
    }
  ];

  // Função para renderizar ações dos cenários (versão mockada)
  const getMockScenarioActions = (scenario: MockScenario) => {
    const items = [];

    // Sempre mostra "Conversas"
    items.push({
      label: "Conversas",
      icon: "pi pi-whatsapp",
      command: () => {
        // Mock - não faz nada
      },
    });

    // "Executar Agora" e "Próxima Execução" só para GoogleMaps e List E que estejam conectados
    if (scenario.is_make_active === true && 
        scenario.data_source && 
        ["list", "googlemaps"].includes(scenario.data_source.toLowerCase()) &&
        scenario.status !== ScenarioStatus.InstanceDisconnected &&
        scenario.connectedPhone &&
        scenario.connectedPhone.toLowerCase() !== "disconnected") {
      items.push({
        label: "Executar Agora",
        icon: "pi pi-bolt",
        command: () => {
          // Mock - não faz nada
        },
      });
    
      items.push({
        label: "Próxima Execução",
        icon: "pi pi-calendar",
        command: () => {
          // Mock - não faz nada
        },
      });
    }

    // "Conectar" se desconectado
    if (!scenario.connectedPhone || 
        scenario.connectedPhone.trim() === "" ||
        scenario.connectedPhone.toLowerCase() === "disconnected" ||
        scenario.status === ScenarioStatus.InstanceDisconnected) {
      items.push({
        label: "Conectar",
        icon: "pi pi-qrcode",
        command: () => {
          // Mock - não faz nada
        },
      });
    }

    // Sempre mostra "Configurar"
    items.push({
      label: "Configurar",
      icon: "pi pi-cog",
      command: () => {
        // Mock - não faz nada
      },
    });

    // "Ativar" se desativado
    if (scenario.is_make_active === false) {
      items.push({
        label: "Ativar",
        icon: "pi pi-play",
        command: () => {
          // Mock - não faz nada
        },
      });
    }

    // "Desativar" se ativado
    if (scenario.is_make_active === true) {
      items.push({
        label: "Desativar",
        icon: "pi pi-pause",
        command: () => {
          // Mock - não faz nada
        },
      });
    }

    // "Desconectar" se conectado
    if (scenario.status !== ScenarioStatus.InstanceDisconnected
        && scenario.connectedPhone 
        && scenario.connectedPhone.toLowerCase() !== "disconnected") {
      items.push({
        label: "Desconectar",
        icon: "pi pi-power-off",
        command: () => {
          // Mock - não faz nada
        },
      });
    }

    // Sempre mostra "Deletar"
    items.push({
      label: "Deletar",
      icon: "pi pi-trash",
      command: () => {
        // Mock - não faz nada
      },
    });

    return (
      <span className="p-buttonset">
        {items.splice(0, 2).map((item, index) => (
          <Button
            key={index}
            label={item.label}
            icon={item.icon}
            onClick={item.command}
            size="small"
            className="p-button-text p-button-plain"
          />
        ))}
        {items.length >= 2 ? (
          <SplitButton
            label={items[0].label}
            icon={items[0].icon}
            dropdownIcon="pi pi-ellipsis-v"
            onClick={items[0].command}
            model={items.splice(1, items.length)}
            size="small"
            className="p-button-text p-button-plain"
            style={{ color: '#5371ff' }}
          />
        ) : (
          <SplitButton
            label={items[0].label}
            icon={items[0].icon}
            onClick={items[0].command}
            model={items.splice(0, items.length)}
            size="small"
            className="p-button-text p-button-plain"
            style={{ color: '#5371ff' }}
          />
        )}
      </span>
    );
  };

  // Componente para renderizar status do cenário
  const renderScenarioStatus = (scenario: MockScenario) => (
    <>
      {scenario.is_make_active === true ? (
        <Tag
          className="text-xs"
          severity="success"
          value="Ativado"
        />
      ) : scenario.is_make_active === false ? (
        <Tag
          className="text-xs"
          severity="warning"
          value="Desativado"
        />
      ) : null}
      {scenario.test_mode && (
        <>
          <br />
          <Tag
            className="text-xs mt-2"
            icon="pi pi-exclamation-triangle"
            severity="warning"
            value="Modo Teste"
          />
        </>
      )}
    </>
  );

  // Componente para renderizar status do WhatsApp
  const renderWhatsAppStatus = (scenario: MockScenario) => (
    <>
      {scenario.status === ScenarioStatus.Running && 
       scenario.connectedPhone &&
       scenario.connectedPhone.trim().toLowerCase() !== "disconnected" ? (
        <Tag
          className="text-xs"
          severity="success"
          value="Conectado"
        />
      ) : scenario.status === ScenarioStatus.Paused ? (
        <Tag
          className="text-xs"
          severity="warning"
          value="Desativado"
        />
      ) : (
        <Tag
          className="text-xs"
          severity="danger"
          value="Desconectado"
        />
      )}
    </>
  );

  // Componente para renderizar número conectado
  const renderConnectedPhone = (scenario: MockScenario) => {
    const shouldBeEmpty = 
      scenario.status === ScenarioStatus.InstanceDisconnected ||
      !scenario.connectedPhone ||
      scenario.connectedPhone.trim().toLowerCase() === "disconnected" ||
      scenario.connectedPhone.trim() === "";
    
    return shouldBeEmpty ? "" : formatPhone(scenario.connectedPhone || "");
  };

  return (
    <>
      <Toast ref={toast} />
      <main className="p-6 w-full">
        {/* Header */}
        <header className="flex justify-content-between align-items-center mb-5 w-full">
          <div className="flex align-items-center">
            <h1 className="text-3xl text-900 font-bold">
              Changelog - DiwoHub
            </h1>
            <Tag 
              value="v2.0.0 - 09/09/2025" 
              severity="info" 
              className="ml-3"
              style={{ backgroundColor: '#5371ff', color: 'white' }}
            />
          </div>
        </header>

        {/* Introdução */}
        <section aria-labelledby="intro-heading">
          <Card className="mb-4">
            <p className="text-lg text-700 line-height-3">
              Apresentamos as últimas melhorias realizadas em nossa plataforma.
              <br />
              Essas atualizações foram pensadas para trazer mais agilidade, clareza e praticidade no seu dia a dia.
            </p>
          </Card>
        </section>

        <Divider />

        {/* Seção 1 - Melhor Desempenho */}
        <section aria-labelledby="performance-heading">
          <Card className="mb-4">
            <header className="flex align-items-center mb-3">
              <i className="pi pi-bolt mr-3 text-2xl" style={{ color: '#5371ff' }} aria-hidden="true" />
              <h2 id="performance-heading" className="text-2xl font-bold text-900 m-0">1. Melhor Desempenho</h2>
            </header>
            <ul className="ml-5 list-none p-0">
              <li className="flex align-items-start mb-3">
                <i className="pi pi-check-circle mr-2 mt-1" style={{ color: '#10b981' }} aria-hidden="true" />
                <p className="m-0">
                  <strong>Carregamento mais rápido:</strong> telas otimizadas para aumentar a velocidade de navegação.
                </p>
              </li>
            </ul>
          </Card>
        </section>

        {/* Seção 2 - Melhor Transparência na Tabela */}
        <section aria-labelledby="table-transparency-heading">
          <Card className="mb-4">
            <header className="flex align-items-center mb-3">
              <i className="pi pi-table mr-3 text-2xl" style={{ color: '#5371ff' }} aria-hidden="true" />
              <h2 id="table-transparency-heading" className="text-2xl font-bold text-900 m-0">2. Melhor Transparência na Tabela</h2>
            </header>
            <ul className="ml-5 list-none p-0">
              <li className="flex align-items-start mb-3">
                <i className="pi pi-check-circle mr-2 mt-1" style={{ color: '#10b981' }} aria-hidden="true" />
                <div>
                  <p className="m-0">
                    <strong>Status da tabela subdividido:</strong> a coluna "Status" foi separada em "Status Cenário" e "Status Whatsapp" para maior clareza sobre o estado de cada componente.
                  </p>
                  <p className="m-0 mt-2 text-600">
                    Agora você pode distinguir facilmente entre o status do cenário (ativado/desativado) e o status da conexão do WhatsApp (conectado/desconectado), proporcionando maior transparência e controle.
                  </p>
                  <figure className="mt-3 p-3 bg-gray-50 border-round m-0">
                    <div className="text-xs text-600 mb-2">
                      <em>Exemplo da nova estrutura da tabela:</em>
                    </div>
                    <DataTable
                      value={mockScenarios.slice(0, 1)}
                      tableStyle={{ minWidth: "50rem" }}
                      size="small"
                    >
                      <Column field="scenario_name" header="Nome" />
                      <Column
                        header="Status Cenário"
                        body={renderScenarioStatus}
                      />
                      <Column
                        header="Status Whatsapp"
                        body={renderWhatsAppStatus}
                      />
                      <Column
                        header="Número Conectado"
                        body={renderConnectedPhone}
                      />
                    </DataTable>
                    <figcaption className="text-xs text-600 mt-2">
                      <em>Separação clara entre status do cenário e status do WhatsApp</em>
                    </figcaption>
                  </figure>
                </div>
              </li>
            </ul>
          </Card>
        </section>

        {/* Seção 3 - Experiência de Uso */}
        <section aria-labelledby="ux-heading">
          <Card className="mb-4">
            <header className="flex align-items-center mb-3">
              <i className="pi pi-palette mr-3 text-2xl" style={{ color: '#5371ff' }} aria-hidden="true" />
              <h2 id="ux-heading" className="text-2xl font-bold text-900 m-0">3. Experiência de Uso</h2>
            </header>
            <ul className="ml-5 list-none p-0">
              <li className="flex align-items-start mb-3">
                <i className="pi pi-check-circle mr-2 mt-1" style={{ color: '#10b981' }} aria-hidden="true" />
                <div>
                  <p className="m-0">
                    <strong>Novas cores de botões:</strong> interface atualizada, tornando a interação mais agradável.
                  </p>
                  <figure className="mt-3 p-3 bg-gray-50 border-round m-0">
                    <div className="flex gap-2 align-items-center">
                      <Button
                        label="Criar"
                        icon="pi pi-plus"
                        size="small"
                        style={{ backgroundColor: '#5371ff', borderColor: '#5371ff' }}
                      />
                      <Button
                        label="Buscar"
                        icon="pi pi-search"
                        size="small"
                        style={{ backgroundColor: '#5371ff', borderColor: '#5371ff' }}
                      />
                      <Button
                        label="Limpar"
                        icon="pi pi-times"
                        size="small"
                        severity="secondary"
                        outlined
                        style={{ color: '#5371ff', borderColor: '#5371ff' }}
                      />
                    </div>
                    <figcaption className="text-xs text-600 mt-2">
                      <em>Demonstração dos novos botões com cores atualizadas</em>
                    </figcaption>
                  </figure>
                </div>
              </li>
              <li className="flex align-items-start mb-3">
                <i className="pi pi-check-circle mr-2 mt-1" style={{ color: '#10b981' }} aria-hidden="true" />
                <div>
                  <p className="m-0">
                    <strong>Barra de busca de cenários:</strong> agora é possível localizar cenários de forma muito mais rápida.
                  </p>
                  <figure className="mt-3 p-3 bg-gray-50 border-round m-0">
                    <div className="flex flex-wrap gap-3 align-items-center">
                      <div className="flex align-items-center">
                        <label htmlFor="demo-search" className="mr-2 font-medium">Buscar:</label>
                        <InputText
                          id="demo-search"
                          value={demoSearch}
                          onChange={(e) => setDemoSearch(e.target.value)}
                          placeholder="Buscar por nome ou telefone..."
                          style={{ width: "300px" }}
                          onKeyDown={(e) => {
                            if (e.key === "Enter") {
                              clearDemoSearch();
                            }
                          }}
                        />
                      </div>
                      <div className="flex gap-2">
                        <Button
                          icon="pi pi-search"
                          label="Buscar"
                          size="small"
                          onClick={clearDemoSearch}
                          style={{ backgroundColor: '#5371ff', borderColor: '#5371ff' }}
                        />
                        <Button
                          icon="pi pi-times"
                          label="Limpar"
                          size="small"
                          severity="secondary"
                          outlined
                          onClick={clearDemoSearch}
                          style={{ color: '#5371ff', borderColor: '#5371ff' }}
                        />
                      </div>
                    </div>
                    <figcaption className="text-xs text-600 mt-2">
                      <em>Nova barra de busca integrada na interface</em>
                    </figcaption>
                  </figure>
                </div>
              </li>
            </ul>
          </Card>
        </section>

        {/* Seção 4 - Detalhes de Cenários */}
        <section aria-labelledby="details-heading">
          <Card className="mb-4">
            <header className="flex align-items-center mb-3">
              <i className="pi pi-info-circle mr-3 text-2xl" style={{ color: '#5371ff' }} aria-hidden="true" />
              <h2 id="details-heading" className="text-2xl font-bold text-900 m-0">4. Detalhes de Cenários</h2>
            </header>
            <ul className="ml-5 list-none p-0">
              <li className="flex align-items-start mb-3">
                <i className="pi pi-check-circle mr-2 mt-1" style={{ color: '#10b981' }} aria-hidden="true" />
                <div>
                  <p className="m-0">
                    <strong>Saldo de créditos disponível:</strong> agora é exibido diretamente na tela de detalhes de créditos, facilitando o acompanhamento.
                  </p>
                  <figure className="mt-3 p-3 bg-gray-50 border-round m-0">
                    <div className="surface-card shadow-2 border-round p-3 flex align-items-center" style={{ 
                      backgroundColor: 'linear-gradient(135deg, #f8f9ff 0%, #e6e9ff 100%)', 
                      border: '1px solid #d1d5ff',
                      minWidth: '400px'
                    }}>
                      <div className="flex align-items-center justify-content-between w-full">
                        <div className="flex align-items-center mr-4">
                          <i className="pi pi-credit-card mr-2" style={{ color: '#5371ff', fontSize: '1.2rem' }} />
                          <span className="font-semibold" style={{ color: '#5371ff' }}>Créditos:</span>
                          <span className="ml-1 font-bold" style={{ color: '#2c3e50' }}>1.500</span>
                        </div>
                        <div className="flex align-items-center mr-4">
                          <i className="pi pi-chart-line mr-2" style={{ color: '#5371ff', fontSize: '1.2rem' }} />
                          <span className="font-semibold" style={{ color: '#5371ff' }}>Consumo:</span>
                          <span className="ml-1 font-bold" style={{ color: '#2c3e50' }}>320</span>
                        </div>
                        <div className="flex align-items-center">
                          <i className="pi pi-wallet mr-2" style={{ color: '#5371ff', fontSize: '1.2rem' }} />
                          <span className="font-semibold" style={{ color: '#5371ff' }}>Saldo:</span>
                          <span className="ml-1 font-bold" style={{ color: '#2c3e50' }}>1.180</span>
                        </div>
                      </div>
                    </div>
                    <figcaption className="text-xs text-600 mt-2">
                      <em>Painel de créditos integrado na tela principal</em>
                    </figcaption>
                  </figure>
                </div>
              </li>
              <li className="flex align-items-start mb-3">
                <i className="pi pi-check-circle mr-2 mt-1" style={{ color: '#10b981' }} aria-hidden="true" />
                <div>
                  <p className="m-0">
                    <strong>Próxima execução em formato de botão:</strong> visual mais prático, especialmente para cenários em lista ou no Google Maps.
                  </p>
                  <figure className="mt-3 p-3 bg-gray-50 border-round m-0">
                    <div className="text-xs text-600 mb-2">
                      <em>Observe que "Executar Agora" e "Próxima Execução" só aparecem para cenários GoogleMaps e List que estejam conectados:</em>
                    </div>
                    <DataTable
                      value={mockScenarios}
                      tableStyle={{ minWidth: "50rem" }}
                      size="small"
                    >
                      <Column field="scenario_name" header="Nome" />
                      <Column
                        header="Status Cenário"
                        body={renderScenarioStatus}
                      />
                      <Column
                        header="Status Whatsapp"
                        body={renderWhatsAppStatus}
                      />
                      <Column
                        header="Número Conectado"
                        body={renderConnectedPhone}
                      />
                      <Column
                        className="text-right"
                        body={getMockScenarioActions}
                      />
                    </DataTable>
                    <figcaption className="text-xs text-600 mt-2">
                      <em>Demonstração da nova tabela com botões contextuais</em>
                    </figcaption>
                  </figure>
                </div>
              </li>
            </ul>
          </Card>
        </section>

        <Divider />

        {/* Footer */}
        <footer>
          <Card>
            <div className="text-center">
              <p className="text-lg text-700 mb-3">
                Essas melhorias fazem parte do nosso compromisso em oferecer uma plataforma cada vez mais eficiente e transparente.
              </p>
              <p className="text-xl font-bold m-0" style={{ color: '#5371ff' }}>
                Equipe DiwoHub
              </p>
            </div>
          </Card>

          {/* Botão de voltar no final */}
          <nav className="text-center mt-5">
            <Button
              label="Voltar para Cenários"
              icon="pi pi-arrow-left"
              onClick={goBack}
              className="p-button-outlined"
              style={{ borderColor: '#5371ff', color: '#5371ff' }}
              aria-label="Voltar para a página de cenários"
            />
          </nav>
        </footer>
      </main>
    </>
  );
}